import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.nma.uat.sr'
const TOKEN_KEY = 'chemtrace_auth_token'

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  userType: 'nma' | 'external'
  nmaRole?: 'admin' | 'manager' | 'staff'
  companies?: Array<{
    id: string
    name: string
    role: string
  }>
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const isExternalUser = computed(() => user.value?.userType === 'external')
  const primaryCompany = computed(() => user.value?.companies?.[0])

  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user: { email, password } }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const message = data.error || data.message || `Login failed (${response.status})`
        throw new Error(message)
      }

      const data = await response.json()
      user.value = data.user

      // Store JWT token for cross-site auth (cookies blocked by browsers)
      if (data.user?.token) {
        localStorage.setItem(TOKEN_KEY, data.user.token)
      }

      return true
    } catch (err: unknown) {
      error.value =  err instanceof Error ? err.message : String(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/v1/logout`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      })
    } finally {
      user.value = null
      localStorage.removeItem(TOKEN_KEY)
    }
  }

  async function fetchCurrentUser(): Promise<User | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      })

      if (!response.ok) {
        if (response.status === 401) {
          user.value = null
          localStorage.removeItem(TOKEN_KEY)
          return null
        }
        throw new Error('Failed to fetch user')
      }

      const data = await response.json()
      user.value = data
      return data
    } catch (err) {
      user.value = null
      return null
    }
  }

  async function checkAuth(): Promise<boolean> {
    if (user.value) return true
    const currentUser = await fetchCurrentUser()
    return currentUser !== null
  }

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isExternalUser,
    primaryCompany,
    login,
    logout,
    fetchCurrentUser,
    checkAuth,
  }
})
